// src/Pages/PartyCallDetailPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPartyCallDetailById,
  clearSelectedPartyCallDetail,
} from "../../../Redux/Slice/Partycalldetails";
import Loader from "../../../Components/Loader/Loader";
import SupportStyleTable from "../../../Components/Table/SupportStyleTable";
import "../../../Components/Table/DynamicTable.css";
import "../../CustomerReq/InhouseRnd.css";
import {
  ArrowLeft,
  Building2,
  Flame,
  Tag,
  CheckCircle2,
  Clock,
  Hourglass,
  PhoneCall,
  Zap,
  Eye,
  AlertCircle,
  Calendar,
} from "lucide-react";

// Curated avatar gradient palette (exact SaaS / support theme)
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #4f46e5, #7c3aed)", // Indigo -> Purple
  "linear-gradient(135deg, #059669, #10b981)", // Emerald -> Teal
  "linear-gradient(135deg, #2563eb, #38bdf8)", // Royal Blue -> Sky
  "linear-gradient(135deg, #d97706, #f59e0b)", // Amber -> Orange
  "linear-gradient(135deg, #0284c7, #06b6d4)", // Sky -> Cyan
  "linear-gradient(135deg, #9333ea, #c026d3)", // Violet -> Magenta
  "linear-gradient(135deg, #0d9488, #14b8a6)", // Teal -> Mint
  "linear-gradient(135deg, #ea580c, #fb923c)", // Orange -> Coral
];

const getAvatarGradient = (str) => {
  if (!str || typeof str !== "string") return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const EmployeeAvatar = ({ photo, name, size = 30 }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [photo]);

  const initials = useMemo(() => {
    if (!name || name === "-") return "EM";
    const clean = name.trim().replace(/[^a-zA-Z0-9\s]/g, "");
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase() || "EM";
  }, [name]);

  const formattedPhoto = useMemo(() => {
    if (!photo) return null;
    const p = String(photo).trim();
    if (
      p.startsWith("http://") ||
      p.startsWith("https://") ||
      p.startsWith("data:") ||
      p.startsWith("blob:")
    ) {
      return p;
    }
    const apiBase = process.env.REACT_APP_API_URL || "";
    if (p.startsWith("/")) {
      return `${apiBase}${p}`;
    }
    return `${apiBase}/${p}`;
  }, [photo]);

  if (formattedPhoto && !hasError) {
    return (
      <img
        src={formattedPhoto}
        alt={name || "Employee"}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          border: "1.5px solid #cbd5e1",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        }}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        borderRadius: "50%",
        flexShrink: 0,
        background: getAvatarGradient(name),
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size <= 28 ? "10px" : "11px",
        fontWeight: "700",
        letterSpacing: "-0.5px",
        textTransform: "uppercase",
        boxShadow: "0 1px 3px rgba(0,0,0,0.14)",
        border: "1.5px solid rgba(255,255,255,0.9)",
      }}
      title={name || "User"}
    >
      {initials}
    </div>
  );
};

const PartyCallDetailPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const detailsType = searchParams.get("details") || "Total";
  const [ageFilter, setAgeFilter] = useState("all");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    selectedPartyCallDetail,
    partyCallDetailLoading,
    partyCallDetailError,
  } = useSelector((state) => state.partyCall);

  useEffect(() => {
    if (id) {
      dispatch(fetchPartyCallDetailById(id));
    }
    return () => {
      dispatch(clearSelectedPartyCallDetail());
    };
  }, [dispatch, id]);

  // Extract accurate call creation timestamp from ObjectId or createdAt or Date
  const getCallTimestamp = (item) => {
    if (!item) return null;

    if (item.createdAt) {
      const d = new Date(item.createdAt);
      if (!isNaN(d.getTime())) return d;
    }

    const idStr = String(item._id || item.id || "");
    if (idStr.length === 24) {
      try {
        const timeFromId = parseInt(idStr.substring(0, 8), 16) * 1000;
        if (!isNaN(timeFromId) && timeFromId > 0) {
          return new Date(timeFromId);
        }
      } catch (e) {
        // ignore
      }
    }

    if (item.Date) {
      const d = new Date(item.Date);
      if (!isNaN(d.getTime())) return d;
    }

    return null;
  };

  const formatDate = (dateString, item) => {
    if (dateString) {
      const d = new Date(dateString);
      if (!isNaN(d.getTime())) {
        return `${String(d.getDate()).padStart(2, "0")}/${String(
          d.getMonth() + 1
        ).padStart(2, "0")}/${d.getFullYear()}`;
      }
    }
    const fallback = getCallTimestamp(item);
    if (fallback) {
      return `${String(fallback.getDate()).padStart(2, "0")}/${String(
        fallback.getMonth() + 1
      ).padStart(2, "0")}/${fallback.getFullYear()}`;
    }
    return "N/A";
  };

  const formatTime = (dateString, item) => {
    if (dateString) {
      const d = new Date(dateString);
      if (!isNaN(d.getTime())) {
        const isMidnightUTC =
          d.getUTCHours() === 0 &&
          d.getUTCMinutes() === 0 &&
          d.getUTCSeconds() === 0;
        if (!isMidnightUTC) {
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          return `${hours}:${minutes} ${ampm}`;
        }
      }
    }

    const realTime = getCallTimestamp(item);
    if (realTime) {
      let hours = realTime.getHours();
      const minutes = String(realTime.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    }

    return "-";
  };

  const formatDuration = (diffMs) => {
    if (!diffMs || diffMs <= 0) return "-";
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHrs = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);

    let parts = [];
    if (diffDays > 0) {
      parts.push(`${diffDays}d`);
      if (diffHrs > 0) parts.push(`${diffHrs}h`);
    } else if (diffHrs > 0) {
      parts.push(`${diffHrs}h`);
      if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}m`);
    } else {
      if (diffMins > 0) parts.push(`${diffMins}m`);
      else parts.push("< 1m");
    }

    return parts.join(" ");
  };

  // 1. Calculate how many days old the call is (Call Age)
  const getCallAgeInfo = (item, callTimestamp) => {
    const now = new Date();
    const ts = callTimestamp || (item.Date ? new Date(item.Date) : null);
    if (!ts || isNaN(ts.getTime())) {
      return { ageDays: 0, ageHours: 0, ageText: "-", ageSeverity: "new" };
    }

    const diffMs = Math.max(0, now.getTime() - ts.getTime());
    const ageDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const ageHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const ageMins = Math.floor((diffMs / (1000 * 60)) % 60);

    let ageText = "";
    let ageSeverity = "new"; // "new", "medium", "old", "critical"

    if (ageDays === 0) {
      if (ageHours === 0) {
        ageText = ageMins <= 5 ? "Just now" : `${ageMins}m ago (Today)`;
      } else {
        ageText = `Today (${ageHours}h ago)`;
      }
      ageSeverity = "new";
    } else if (ageDays === 1) {
      ageText = `1 day old (Yesterday)`;
      ageSeverity = "new";
    } else if (ageDays <= 5) {
      ageText = `${ageDays} days old`;
      ageSeverity = "medium";
    } else if (ageDays <= 15) {
      ageText = `${ageDays} days old`;
      ageSeverity = "old";
    } else {
      ageText = `${ageDays} days old`;
      ageSeverity = "critical";
    }

    return {
      ageDays,
      ageHours,
      ageText,
      ageSeverity,
      ageMs: diffMs,
    };
  };

  // 2. Calculate working duration / days in progress if status is Working/Accept
  const getWorkingInfo = (item, callTimestamp) => {
    const rawStatus = String(item?.Status || "").toLowerCase().trim();
    const isWorking =
      rawStatus === "accept" ||
      rawStatus === "accepted" ||
      rawStatus === "working" ||
      rawStatus === "running";

    if (!isWorking) {
      return {
        isWorking: false,
        workingDays: 0,
        workingHours: 0,
        workingMinutes: 0,
        workingText: "-",
        workingSortValue: 0,
      };
    }

    const now = new Date();
    let start = null;

    // A. Check TimeSlabRecord for latest Accept entry
    if (Array.isArray(item.TimeSlabRecord) && item.TimeSlabRecord.length > 0) {
      const acceptSlabs = item.TimeSlabRecord.filter(
        (s) => s && String(s.type || "").toLowerCase() === "accept" && s.Date
      );
      if (acceptSlabs.length > 0) {
        const lastSlab = acceptSlabs[acceptSlabs.length - 1];
        const d = new Date(lastSlab.Date);
        if (!isNaN(d.getTime()) && d <= now) {
          start = d;
        }
      }
    }

    // B. Check Record array for accept/working entry
    if (!start && Array.isArray(item.Record) && item.Record.length > 0) {
      const acceptRec = [...item.Record]
        .reverse()
        .find(
          (r) =>
            r &&
            (String(r.DescripTione || "").toLowerCase().includes("accept") ||
              String(r.DescripTione || "").toLowerCase().includes("working")) &&
            r.Date
        );
      if (acceptRec?.Date) {
        const d = new Date(acceptRec.Date);
        if (!isNaN(d.getTime()) && d <= now) {
          start = d;
        }
      }
      if (!start) {
        const lastRec = item.Record[item.Record.length - 1];
        if (lastRec?.Date) {
          const d = new Date(lastRec.Date);
          if (!isNaN(d.getTime()) && d <= now) {
            start = d;
          }
        }
      }
    }

    // C. Fallback to call creation timestamp
    if (!start) {
      start = callTimestamp || (item.Date ? new Date(item.Date) : null);
    }

    if (!start || isNaN(start.getTime())) {
      return {
        isWorking: true,
        workingDays: 0,
        workingHours: 0,
        workingMinutes: 0,
        workingText: "Working",
        workingSortValue: 1,
      };
    }

    const diffMs = Math.max(0, now.getTime() - start.getTime());
    const workingDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const workingHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const workingMinutes = Math.floor((diffMs / (1000 * 60)) % 60);

    let workingText = "";
    if (workingDays === 0) {
      if (workingHours === 0) {
        workingText = `${workingMinutes}m`;
      } else {
        workingText = `${workingHours}h ${workingMinutes}m`;
      }
    } else if (workingDays === 1) {
      workingText = `1d ${workingHours}h`;
    } else {
      workingText = `${workingDays}d ${workingHours}h`;
    }

    return {
      isWorking: true,
      workingDays,
      workingHours,
      workingMinutes,
      workingText,
      workingSortValue: diffMs,
      workingStartDate: start,
    };
  };

  const calculateTerm = (startDate, endDate, status, item) => {
    const s = String(status || "").toLowerCase().trim();
    if (s === "completed" || s === "complete") {
      if (!startDate || !endDate) return "-";
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (isNaN(start) || isNaN(end) || end < start) return "-";
      return formatDuration(end - start);
    }
    return "-";
  };

  // Company Information
  const companyName = useMemo(() => {
    if (selectedPartyCallDetail?.companyInfo?.name) {
      return selectedPartyCallDetail.companyInfo.name;
    }
    const firstCall =
      selectedPartyCallDetail?.Total?.[0] ||
      selectedPartyCallDetail?.Complete?.[0] ||
      selectedPartyCallDetail?.Panding?.[0];
    return (
      firstCall?.CompanyName?.WhaClientName ||
      firstCall?.CompanyName?.CompanyName ||
      "Company Call Details"
    );
  }, [selectedPartyCallDetail]);

  const companyType = useMemo(() => {
    return (
      selectedPartyCallDetail?.companyInfo?.type ||
      selectedPartyCallDetail?.Total?.[0]?.CompanyName?.Type ||
      ""
    );
  }, [selectedPartyCallDetail]);

  const isPriority = useMemo(() => {
    const priority =
      selectedPartyCallDetail?.companyInfo?.priority ||
      selectedPartyCallDetail?.Total?.[0]?.CompanyName?.Priority ||
      "";
    return String(priority).toLowerCase() === "on";
  }, [selectedPartyCallDetail]);

  // Counts for tabs
  const counts = useMemo(() => {
    return {
      Total: selectedPartyCallDetail?.Total?.length || 0,
      Pending: selectedPartyCallDetail?.Panding?.length || 0,
      Completed: selectedPartyCallDetail?.Complete?.length || 0,
      Accept: selectedPartyCallDetail?.Accept?.length || 0,
      "Solve By Dynamic": selectedPartyCallDetail?.Solve_By_Dynamic?.length || 0,
    };
  }, [selectedPartyCallDetail]);

  const getDataArray = () => {
    if (!selectedPartyCallDetail) return [];

    switch (detailsType) {
      case "Total":
        return selectedPartyCallDetail.Total || [];
      case "Pending":
        return selectedPartyCallDetail.Panding || [];
      case "Completed":
        return selectedPartyCallDetail.Complete || [];
      case "Accept":
        return selectedPartyCallDetail.Accept || [];
      case "Solve By Dynamic":
        return selectedPartyCallDetail.Solve_By_Dynamic || [];
      default:
        return selectedPartyCallDetail.Total || [];
    }
  };

  const handleTabClick = (type) => {
    setSearchParams({ details: type });
  };

  const handleRowClick = (row) => {
    navigate(`/partycalldetails/${row.id}/${id}`);
  };

  // Latest calls on top: Sort descending by Date / createdAt / CallNo
  const sortedCalls = useMemo(() => {
    const raw = getDataArray();
    return [...raw].sort((a, b) => {
      const timeA =
        getCallTimestamp(a)?.getTime() ||
        (a.Date ? new Date(a.Date).getTime() : 0);
      const timeB =
        getCallTimestamp(b)?.getTime() ||
        (b.Date ? new Date(b.Date).getTime() : 0);
      if (timeB !== timeA) return timeB - timeA;

      const callA = parseInt(String(a.CallNo || "").replace(/\D/g, "") || "0", 10);
      const callB = parseInt(String(b.CallNo || "").replace(/\D/g, "") || "0", 10);
      if (callB !== callA) return callB - callA;

      return String(b._id || "").localeCompare(String(a._id || ""));
    });
  }, [selectedPartyCallDetail, detailsType]);

  const rowsBase = useMemo(() => {
    return sortedCalls.map((item, index) => {
      // 1. Task Generator resolution
      const generatorUser =
        item.InPutId ||
        (Array.isArray(item.Record) && item.Record.length > 0
          ? item.Record[0]?.InHouseId
          : null);

      const generatorName =
        generatorUser?.Name ||
        generatorUser?.OwnerName ||
        (typeof generatorUser === "string" ? generatorUser : "") ||
        item.Employee ||
        item.ContactPerName ||
        "-";

      const generatorPhoto =
        generatorUser?.ProfilePhoto ||
        generatorUser?.Photo ||
        generatorUser?.profile ||
        "";

      const generatorRole = generatorUser?.Role || "";
      const isGenSupport = generatorRole.toLowerCase().includes("support");
      const generatorRoleType =
        generatorName === "-" || !generatorName
          ? ""
          : isGenSupport
            ? "Support Team"
            : generatorRole
              ? "Employee"
              : item.ContactPerName === generatorName
                ? "Client Caller"
                : "Generator";

      // 2. Assigned To resolution
      const assignedUser =
        item.SuportTeamId ||
        item.SeniorDevloperID ||
        (Array.isArray(item.Record) && item.Record.length > 0
          ? item.Record[item.Record.length - 1]?.InHouseId
          : null) ||
        item.Solve_By;

      const assignedName =
        assignedUser?.Name ||
        assignedUser?.OwnerName ||
        (typeof assignedUser === "string" ? assignedUser : "-");

      const assignedPhoto =
        assignedUser?.ProfilePhoto ||
        assignedUser?.Photo ||
        assignedUser?.profile ||
        "";

      const assignedRole = assignedUser?.Role || "";
      const isSupport = assignedRole.toLowerCase().includes("support");
      const assignedRoleType =
        assignedName === "-" ? "" : isSupport ? "Support Team" : "Employee";

      const displayReq =
        (item.Requirement || "").trim() ||
        (item.PresentStatus || "").trim() ||
        "-";

      const callTimestamp = getCallTimestamp(item);
      const ageInfo = getCallAgeInfo(item, callTimestamp);
      const workingInfo = getWorkingInfo(item, callTimestamp);

      const sLower = String(item.Status || "").toLowerCase().trim();
      const isCompleted = sLower === "completed" || sLower === "complete";
      const isPending = sLower === "pending" || sLower === "panding";

      return {
        id: item._id,
        srNo: index + 1,
        callNo: item.CallNo || "N/A",
        companyName:
          item.CompanyName?.WhaClientName ||
          item.CompanyName?.CompanyName ||
          "N/A",
        rawDate: callTimestamp
          ? callTimestamp.getTime()
          : item.Date
            ? new Date(item.Date).getTime()
            : 0,
        requestDate: formatDate(item.Date, item),
        requestTime: formatTime(item.Date, item),
        ageDays: ageInfo.ageDays,
        ageHours: ageInfo.ageHours,
        ageText: ageInfo.ageText,
        ageSeverity: ageInfo.ageSeverity,
        ageMs: ageInfo.ageMs,
        isWorking: workingInfo.isWorking,
        workingDays: workingInfo.workingDays,
        workingHours: workingInfo.workingHours,
        workingMinutes: workingInfo.workingMinutes,
        workingText: workingInfo.workingText,
        workingSortValue: workingInfo.workingSortValue,
        isCompleted,
        isPending,
        project: item.Project?.project_name || item.Project?.ProjectName || "N/A",
        requirement: displayReq,
        generatorName,
        generatorPhoto,
        generatorRole,
        generatorRoleType,
        contactPerson: item.ContactPerName || item.ContactPer || "",
        assignedToName: assignedName,
        assignedPhoto: assignedPhoto,
        assignedRole: assignedRole,
        assignedRoleType: assignedRoleType,
        status: item.Status || "Pending",
        term: calculateTerm(
          callTimestamp || item.Date,
          item.Complate_Date,
          item.Status,
          item
        ),
      };
    });
  }, [sortedCalls]);

  // Age based counts for filter toolbar
  const ageCounts = useMemo(() => {
    let all = rowsBase.length;
    let today = 0;
    let week = 0;
    let days7to15 = 0;
    let days15plus = 0;
    let days30plus = 0;

    for (const r of rowsBase) {
      const d = r.ageDays || 0;
      if (d === 0) today++;
      if (d <= 7) week++;
      if (d >= 7 && d < 15) days7to15++;
      if (d >= 15) days15plus++;
      if (d >= 30) days30plus++;
    }

    return { all, today, week, days7to15, days15plus, days30plus };
  }, [rowsBase]);

  // Filtered rows by Call Age
  const rows = useMemo(() => {
    if (ageFilter === "today") return rowsBase.filter((r) => r.ageDays === 0);
    if (ageFilter === "week") return rowsBase.filter((r) => r.ageDays <= 7);
    if (ageFilter === "7to15")
      return rowsBase.filter((r) => r.ageDays >= 7 && r.ageDays < 15);
    if (ageFilter === "15d+") return rowsBase.filter((r) => r.ageDays >= 15);
    if (ageFilter === "30d+") return rowsBase.filter((r) => r.ageDays >= 30);
    return rowsBase;
  }, [rowsBase, ageFilter]);

  const ageFilterOptions = [
    {
      key: "all",
      label: "All Calls",
      count: ageCounts.all,
      activeClass: "bg-slate-800 text-white border-slate-900",
      badgeClass: "bg-slate-100 text-slate-700",
    },
    {
      key: "today",
      label: "Today",
      count: ageCounts.today,
      activeClass: "bg-emerald-600 text-white border-emerald-700",
      badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    },
    {
      key: "week",
      label: "< 7 Days (Week)",
      count: ageCounts.week,
      activeClass: "bg-sky-600 text-white border-sky-700",
      badgeClass: "bg-sky-50 text-sky-700 border border-sky-200",
    },
    {
      key: "7to15",
      label: "7 - 15 Days",
      count: ageCounts.days7to15,
      activeClass: "bg-indigo-600 text-white border-indigo-700",
      badgeClass: "bg-indigo-50 text-indigo-700 border border-indigo-200",
    },
    {
      key: "15d+",
      label: "15 Days +",
      count: ageCounts.days15plus,
      activeClass: "bg-amber-600 text-white border-amber-700",
      badgeClass: "bg-amber-50 text-amber-800 border border-amber-200 font-bold",
    },
    {
      key: "30d+",
      label: "30 Days +",
      count: ageCounts.days30plus,
      activeClass: "bg-rose-600 text-white border-rose-700",
      badgeClass: "bg-rose-50 text-rose-700 border border-rose-200 font-extrabold",
    },
  ];

  const columns = [
    {
      headerName: "Sr. No",
      field: "srNo",
      flex: 0.5,
      sortable: true,
      sortValue: (row) => row.srNo,
    },
    {
      headerName: "Call No",
      field: "callNo",
      flex: 0.8,
      sortable: true,
      sortValue: (row) =>
        parseInt(String(row.callNo).replace(/\D/g, "") || "0", 10),
      renderCell: ({ value }) => {
        const cleanVal = String(value || "").replace(/^#/, "");
        return (
          <span
            className="badge rounded-pill px-2.5 py-1.5 fw-bold shadow-sm inline-flex items-center justify-center font-bold"
            style={{
              backgroundColor: "#EFF6FF",
              color: "#1D4ED8",
              border: "1px solid #BFDBFE",
              borderRadius: "9999px",
              fontSize: "0.82rem",
              fontWeight: "700",
              letterSpacing: "0.02em",
              boxShadow: "0 1px 2px rgba(29, 78, 216, 0.08)",
            }}
          >
            #{cleanVal || "--"}
          </span>
        );
      },
    },
    {
      headerName: "Request Date & Age",
      field: "requestDate",
      flex: 1.4,
      sortable: true,
      sortValue: (row) => row.rawDate,
      renderCell: ({ row }) => (
        <div className="flex flex-col gap-1 py-0.5">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
            <span>{row.requestDate}</span>
            <span className="text-[10.5px] font-medium text-slate-500">
              {row.requestTime}
            </span>
          </div>
          {/* 📅 Age Badge (kitne din purana hai) */}
          <div className="flex items-center">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-bold border ${row.ageSeverity === "new"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : row.ageSeverity === "medium"
                  ? "bg-sky-50 text-sky-700 border-sky-200"
                  : row.ageSeverity === "old"
                    ? "bg-amber-50 text-amber-800 border-amber-300"
                    : "bg-rose-50 text-rose-700 border-rose-300 font-extrabold"
                }`}
              title={`Call logged on ${row.requestDate} (${row.ageDays} days ago)`}
            >
              <Clock size={11} />
              <span>{row.ageText}</span>
            </span>
          </div>
        </div>
      ),
    },
    {
      headerName: "Project",
      field: "project",
      flex: 1.0,
      sortable: true,
      renderCell: ({ value }) =>
        value && value !== "N/A" ? (
          <span className="text-[11px] font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md border border-slate-200 truncate inline-block max-w-[140px]">
            {value}
          </span>
        ) : (
          <span className="text-slate-400">-</span>
        ),
    },
    {
      headerName: "Requirement / Issue",
      field: "requirement",
      flex: 2.2,
      sortable: true,
      renderCell: ({ value }) => (
        <div
          className="max-w-[320px] truncate text-xs text-slate-700 font-normal leading-relaxed"
          title={value}
        >
          {value}
        </div>
      ),
    },
    {
      headerName: "Task Generator",
      field: "generatorName",
      flex: 1.4,
      sortable: true,
      renderCell: ({ row }) =>
        row.generatorName && row.generatorName !== "-" ? (
          <div className="flex items-center gap-2 py-0.5 min-w-0">
            <EmployeeAvatar
              photo={row.generatorPhoto}
              name={row.generatorName}
              size={28}
            />
            <div className="flex flex-col min-w-0">
              <span
                className="font-bold text-slate-900 text-xs truncate max-w-[140px] leading-tight"
                title={`Generated by: ${row.generatorName}${row.generatorRole ? ` (${row.generatorRole})` : ""
                  }`}
              >
                {row.generatorName}
              </span>
              <span
                className={`text-[10px] font-semibold truncate leading-tight mt-0.5 ${row.generatorRoleType === "Support Team"
                  ? "text-indigo-700"
                  : row.generatorRoleType === "Client Caller"
                    ? "text-amber-700"
                    : "text-purple-700"
                  }`}
              >
                {row.generatorRoleType || "Generator"}
                {row.generatorRole ? ` • ${row.generatorRole}` : ""}
              </span>
            </div>
          </div>
        ) : (
          <span className="text-slate-400 text-xs font-medium">-</span>
        ),
    },
    {
      headerName: "Assigned To",
      field: "assignedToName",
      flex: 1.4,
      sortable: true,
      renderCell: ({ row }) =>
        row.assignedToName && row.assignedToName !== "-" ? (
          <div className="flex items-center gap-2 py-0.5 min-w-0">
            <EmployeeAvatar
              photo={row.assignedPhoto}
              name={row.assignedToName}
              size={28}
            />
            <div className="flex flex-col min-w-0">
              <span
                className="font-bold text-slate-900 text-xs truncate max-w-[140px] leading-tight"
                title={`${row.assignedToName}${row.assignedRole ? ` (${row.assignedRole})` : ""
                  }`}
              >
                {row.assignedToName}
              </span>
              <span
                className={`text-[10px] font-semibold truncate leading-tight mt-0.5 ${row.assignedRoleType === "Support Team"
                  ? "text-emerald-700"
                  : "text-blue-700"
                  }`}
              >
                {row.assignedRoleType}
                {row.assignedRole ? ` • ${row.assignedRole}` : ""}
              </span>
            </div>
          </div>
        ) : (
          <span className="badge rounded-pill px-2.5 py-1 text-[11px] font-normal bg-red-50 text-red-600 border border-red-200">
            ⏳ Not Assigned
          </span>
        ),
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1.1,
      sortable: true,
      renderCell: ({ row, value }) => {
        const rawStatus = value || row?.status || "Pending";
        const sLower = rawStatus.toLowerCase().trim();

        let label = rawStatus;
        let bg = "#FEF3C7";
        let text = "#92400E";
        let border = "#FDE68A";
        let dot = "#D97706";
        let isWorking = false;

        if (sLower === "completed" || sLower === "complete") {
          label = "Completed";
          bg = "#D1FAE5";
          text = "#065F46";
          border = "#A7F3D0";
          dot = "#10B981";
        } else if (
          sLower === "accept" ||
          sLower === "accepted" ||
          sLower === "working" ||
          sLower === "running"
        ) {
          label = "Working";
          bg = "#EFF6FF";
          text = "#1D4ED8";
          border = "#BFDBFE";
          dot = "#2563EB";
          isWorking = true;
        } else if (sLower.includes("hold")) {
          label = "On Hold";
          bg = "#F1F5F9";
          text = "#475569";
          border = "#CBD5E1";
          dot = "#64748B";
        } else if (sLower.includes("senior")) {
          label = "Checking to Senior";
          bg = "#E0E7FF";
          text = "#3730A3";
          border = "#C7D2FE";
          dot = "#6366F1";
        } else if (sLower.includes("support")) {
          label = "Checking to Support";
          bg = "#E0E7FF";
          text = "#3730A3";
          border = "#C7D2FE";
          dot = "#6366F1";
        } else if (sLower.includes("solve") || sLower.includes("dynamic")) {
          label = "Solved By Dynamic";
          bg = "#CFFAFE";
          text = "#0E7490";
          border = "#A5F3FC";
          dot = "#06B6D4";
        } else if (sLower.includes("reject")) {
          label = "Rejected";
          bg = "#FEE2E2";
          text = "#991B1B";
          border = "#FECACA";
          dot = "#EF4444";
        } else {
          label = "Pending";
        }

        return (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold shadow-2xs transition-all"
            style={{
              backgroundColor: bg,
              color: text,
              border: `1px solid ${border}`,
            }}
          >
            {isWorking ? (
              <span className="relative flex h-2 w-2 mr-0.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
            ) : (
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: dot,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
            )}
            <span className={isWorking ? "font-bold tracking-tight" : ""}>
              {label}
            </span>
          </span>
        );
      },
    },
    {
      headerName: "Working / Duration",
      field: "workingDisplay",
      flex: 1.5,
      sortable: true,
      sortValue: (row) => row.workingSortValue || (row.isCompleted ? 1 : 0),
      renderCell: ({ row }) => {
        // ⚡ If task is in Working/Accept status: show cleanly in English, once only
        if (row.isWorking) {
          const durationStr =
            row.workingDays > 0
              ? `${row.workingDays}d ${row.workingHours}h`
              : row.workingHours > 0
                ? `${row.workingHours}h ${row.workingMinutes}m`
                : `${row.workingMinutes}m`;

          return (
            <div className="py-0.5">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border-2 ${row.workingDays >= 7
                  ? "bg-rose-50 text-rose-900 border-rose-400 shadow-2xs"
                  : row.workingDays >= 3
                    ? "bg-amber-50 text-amber-900 border-amber-400 shadow-2xs"
                    : "bg-blue-50 text-blue-900 border-blue-400 shadow-2xs"
                  }`}
                title={`Working duration: ${row.workingDays} days, ${row.workingHours} hours, ${row.workingMinutes} mins`}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-700"></span>
                </span>
                <span className="font-extrabold tracking-tight">
                  ⚡ Working: {durationStr}
                </span>
              </span>
            </div>
          );
        }

        // If completed: show resolution time
        if (row.isCompleted) {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
              ✅ Solved ({row.term && row.term !== "-" ? row.term : "Done"})
            </span>
          );
        }

        // If pending: show how many days pending
        if (row.isPending) {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
              ⏳ Waiting ({row.ageDays}d old)
            </span>
          );
        }

        return (
          <span className="text-xs text-slate-600 font-medium">
            {row.term && row.term !== "-" ? row.term : "-"}
          </span>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          className="action-btn action-btn-show shadow-xs inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-lg cursor-pointer"
          title="Open call details"
        >
          <Eye size={12} />
          <span>View</span>
        </button>
      ),
    },
  ];

  const getRowClassName = (row) => {
    const s = String(row.status || "").toLowerCase().trim();
    if (s.includes("complete") || s === "completed") return "row-completed-subtle";
    if (
      s.includes("accept") ||
      s === "accepted" ||
      s.includes("working") ||
      s.includes("running")
    )
      return "row-accepted-subtle";
    if (s.includes("solve") || s.includes("dynamic")) return "row-solved-subtle";
    if (s.includes("check") || s.includes("senior")) return "row-checking-subtle";
    if (s.includes("reject") || s === "rejected") return "row-rejected-subtle";
    if (s.includes("hold")) return "row-hold-subtle";
    if (s.includes("panding") || s.includes("pending")) return "row-pending-subtle";
    return "";
  };

  const tabOptions = [
    {
      key: "Total",
      label: "Total Requests",
      count: counts.Total,
      icon: <PhoneCall size={13} />,
    },
    {
      key: "Pending",
      label: "Pending",
      count: counts.Pending,
      icon: <Hourglass size={13} />,
    },
    {
      key: "Completed",
      label: "Completed",
      count: counts.Completed,
      icon: <CheckCircle2 size={13} />,
    },
    {
      key: "Accept",
      label: "Working",
      count: counts.Accept,
      icon: <Zap size={13} />,
    },
  ];

  if (partyCallDetailLoading || !selectedPartyCallDetail) {
    return <Loader />;
  }

  if (partyCallDetailError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 text-center">
        <div className="bg-white p-6 rounded-2xl border border-red-200 shadow-xs max-w-md w-full">
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
            <AlertCircle size={24} />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1">
            Unable to load details
          </h3>
          <p className="text-xs text-red-600 mb-4">{partyCallDetailError}</p>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => navigate("/partycalldetails")}
              className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all cursor-pointer"
            >
              Back to Parties
            </button>
            <button
              onClick={() => id && dispatch(fetchPartyCallDetailById(id))}
              className="px-4 py-2 text-xs font-bold text-white bg-[#133A69] hover:bg-[#0d284a] rounded-lg transition-all cursor-pointer shadow-xs"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col space-y-2.5 py-1 px-1">
      {/* Top Header Card */}
      <div className="bg-white px-4 py-2.5 rounded-xl border border-slate-200/90 shadow-2xs space-y-2 shrink-0">
        {/* Navigation Breadcrumb & Back Button */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5">
          <button
            onClick={() => navigate("/partycalldetails")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#133A69] bg-blue-50/80 hover:bg-blue-100/80 border border-blue-200 rounded-lg shadow-2xs transition-all cursor-pointer"
          >
            <ArrowLeft size={13} />
            <span>Back to Parties</span>
          </button>

          <div className="text-xs text-slate-500 font-medium truncate flex items-center gap-1.5">
            <span>Party Call Details</span>
            <span>&gt;</span>
            <span className="font-bold text-[#133A69]">{companyName}</span>
          </div>
        </div>

        {/* Company Title & Badges + Status Switcher Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-0.5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#133A69] to-[#0d284a] text-white flex items-center justify-center shrink-0 shadow-xs">
              <Building2 size={18} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight truncate max-w-xs md:max-w-md">
                  {companyName}
                </h1>
                {isPriority && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-300 shrink-0">
                    <Flame size={11} className="text-rose-500" /> Priority
                  </span>
                )}
                {companyType && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                    <Tag size={10} className="text-slate-500" /> {companyType}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Showing <strong className="text-slate-800">{rows.length}</strong>
                {ageFilter !== "all" && <span> of {rowsBase.length}</span>}{" "}
                {detailsType === "Accept" ? "Working" : detailsType} records
                {ageFilter !== "all" && (
                  <span className="ml-1.5 text-[11px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                    Filtered:{" "}
                    {ageFilterOptions.find((o) => o.key === ageFilter)?.label}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Quick Filter Status Tabs */}
          <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-100/90 rounded-xl border border-slate-200/90 shadow-inner shrink-0">
            {tabOptions.map((tab) => {
              const isActive = detailsType === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleTabClick(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer border-2 ${isActive
                    ? "bg-gradient-to-r from-[#133A69] to-[#1e4e8c] text-white border-[#0d284a] shadow-xs"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[#133A69] hover:text-[#133A69] shadow-2xs hover:-translate-y-0.5"
                    }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${isActive
                      ? "bg-white text-[#133A69]"
                      : "bg-slate-100 text-slate-700 border border-slate-200"
                      }`}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 3: Call Age Filter Bar (Week, 15d+, 30d+, etc.) */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
            <Clock size={13} className="text-[#133A69]" />
            <span>Call Age Filter:</span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {ageFilterOptions.map((opt) => {
              const isActive = ageFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setAgeFilter(opt.key)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer border-2 select-none ${isActive
                    ? `${opt.activeClass} shadow-xs -translate-y-0.5`
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50 shadow-2xs hover:-translate-y-0.5"
                    }`}
                  title={`Filter calls by ${opt.label}`}
                >
                  <span>{opt.label}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${isActive ? "bg-white/25 text-white" : opt.badgeClass
                      }`}
                  >
                    {opt.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Table Card using SupportStyleTable */}
      <div className="bg-white rounded-xl border border-slate-200/90 shadow-2xs p-2 sm:p-2.5 flex-1 min-h-0 flex flex-col">
        {rows.length > 0 ? (
          <SupportStyleTable
            columns={columns}
            rows={rows}
            tableId={`party_call_${id}_${detailsType}_${ageFilter}`}
            fileName={`${companyName}_${detailsType}_Requests`}
            onRowClick={handleRowClick}
            itemsPerPage={50}
            getRowClassName={getRowClassName}
            fullHeight={true}
          />
        ) : (
          <div className="text-center py-16 text-slate-500">
            <PhoneCall className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="font-semibold text-slate-700 text-sm">
              No {detailsType.toLowerCase()} requests found
            </p>
            <p className="text-xs text-slate-400 mt-1">
              There are currently no {detailsType.toLowerCase()} records logged for {companyName}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartyCallDetailPage;
